import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { DialogFooter } from "@/components/ui/dialog";
import MultiSelect from "@/components/MultiSelect";
import { Search, Ban, RotateCcw, Trash2, KeyRound, Eye, EyeOff, History, Timer } from "lucide-react";

const statusStyles = {
  active: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  suspended: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30",
  deleted: "bg-destructive/10 text-destructive border-destructive/30",
  expired: "bg-destructive/10 text-destructive border-destructive/40",
  pending: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30",
  inactive: "bg-muted text-muted-foreground border-border",
};

function CredentialDialog({ user, open, onOpenChange }) {
  const [cred, setCred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!open || !user) { setCred(null); setShow(false); return; }
    api.get(`/admin/customers/${user.id}/credential`).then((r) => setCred(r.data)).catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover" data-testid="credential-modal">
        <DialogHeader>
          <DialogTitle>Credentials for {user?.name}</DialogTitle>
          <DialogDescription>
            The customer's password is encrypted at rest. Toggle to view. Never share via unsecured channels.
          </DialogDescription>
        </DialogHeader>
        {!cred ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Email</div>
              <div className="num mt-0.5" data-testid="cred-email">{cred.email}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Phone</div>
              <div className="num mt-0.5" data-testid="cred-phone">{cred.phone || "—"}</div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Password</div>
                {cred.password && (
                  <Button size="sm" variant="ghost" onClick={() => setShow((s) => !s)} data-testid="cred-toggle">
                    {show ? <><EyeOff className="h-3.5 w-3.5 mr-1" /> Hide</> : <><Eye className="h-3.5 w-3.5 mr-1" /> Show</>}
                  </Button>
                )}
              </div>
              <div className="mt-1 rounded-md border border-border bg-muted/30 px-3 py-2 num" data-testid="cred-password">
                {cred.password ? (show ? cred.password : "•".repeat(Math.max(8, cred.password.length))) : (
                  <span className="text-muted-foreground text-xs">Not retrievable (legacy account or created before encryption). Ask the customer to reset via the dashboard.</span>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ user, open, onOpenChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!open || !user) { setItems([]); return; }
    api.get(`/admin/download-history`, { params: { user_id: user.id } }).then((r) => setItems(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-popover" data-testid="history-modal">
        <DialogHeader>
          <DialogTitle>Download history · {user?.name}</DialogTitle>
          <DialogDescription>Every download this customer has made, with timestamp and IP address.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto border border-border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground sticky top-0">
              <tr>
                <th className="text-left px-3 py-2.5">When</th>
                <th className="text-left px-3 py-2.5">Filename</th>
                <th className="text-left px-3 py-2.5">Kind</th>
                <th className="text-right px-3 py-2.5">Records</th>
                <th className="text-left px-3 py-2.5">IP</th>
              </tr>
            </thead>
            <tbody>
              {items.map((h) => (
                <tr key={h.id} className="border-t border-border" data-testid={`history-row-${h.id}`}>
                  <td className="px-3 py-2 num">{new Date(h.downloaded_at).toLocaleString()}</td>
                  <td className="px-3 py-2">{h.filename}</td>
                  <td className="px-3 py-2 capitalize">{h.kind}</td>
                  <td className="px-3 py-2 text-right num">{(h.record_count || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 num text-muted-foreground">{h.ip}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No downloads yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExtendDialog({ user, open, onOpenChange, onDone }) {
  const [form, setForm] = useState(null);
  const [options, setOptions] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/filters/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open) {
      const sub = user?.subscription;
      setForm({
        data_type: sub?.data_type || "both",
        gst_coverage: sub?.gst_coverage ? { ...sub.gst_coverage } : { type: "pan_india", states: [], cities: [] },
        mca_coverage: sub?.mca_coverage ? { ...sub.mca_coverage } : { type: "pan_india", states: [], districts: [], rocs: [] },
        days: sub?.duration_days || 30,
      });
    }
  }, [open, user]);

  const sub = user?.subscription;
  const isActive = sub?.expires_at ? new Date(sub.expires_at) > new Date() : false;

  const gstCityOpts = useMemo(() => {
    if (!options || !form?.gst_coverage?.states) return [];
    const s = new Set();
    form.gst_coverage.states.forEach((st) => (options.gst_state_cities[st] || []).forEach((c) => s.add(c)));
    return Array.from(s).sort();
  }, [options, form]);

  const mcaDistrictOpts = useMemo(() => {
    if (!options || !form?.mca_coverage?.states) return [];
    const s = new Set();
    form.mca_coverage.states.forEach((st) => (options.mca_state_districts[st] || []).forEach((d) => s.add(d)));
    return Array.from(s).sort();
  }, [options, form]);

  const submit = async () => {
    if (!user?.id || !form) return;
    setBusy(true);
    try {
      const payload = { data_type: form.data_type, days: Number(form.days) || 30 };
      if (form.data_type !== "mca") payload.gst_coverage = form.gst_coverage;
      if (form.data_type !== "gst") payload.mca_coverage = form.mca_coverage;
      await api.post(`/admin/customers/${user.id}/renew`, payload);
      toast.success(isActive
        ? `Extended by ${form.days} day(s) from current expiry`
        : `Renewed for ${form.days} day(s) from today`);
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-popover max-h-[90vh] overflow-y-auto" data-testid="extend-modal">
        <DialogHeader>
          <DialogTitle>Extend / Renew Package · {user?.name}</DialogTitle>
          <DialogDescription>
            {isActive
              ? "Active subscription — new expiry will be added to the current expiry (no service interruption)."
              : sub
                ? "Expired subscription — new expiry starts from today."
                : "No prior subscription — a new approved subscription will be created."}
            {sub?.expires_at && (
              <span className="block mt-1 text-xs num">Current expiry: {new Date(sub.expires_at).toLocaleDateString()}</span>
            )}
          </DialogDescription>
        </DialogHeader>
        {form && options && (
          <div className="space-y-4">
            <div>
              <Label>Data type</Label>
              <RadioGroup value={form.data_type} onValueChange={(v) => setForm({ ...form, data_type: v })} className="grid grid-cols-3 gap-2 mt-2">
                {["gst", "mca", "both"].map((v) => (
                  <label key={v} className={`cursor-pointer rounded-md border p-2 text-center text-sm ${form.data_type === v ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`extend-dtype-${v}`}>
                    <RadioGroupItem value={v} className="hidden" />
                    {v.toUpperCase()}
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="extend-days">Subscription duration (days)</Label>
              <Input
                id="extend-days"
                type="number"
                min={1}
                max={3650}
                value={form.days}
                onChange={(e) => setForm({ ...form, days: e.target.value })}
                data-testid="extend-days-input"
              />
              <div className="text-xs text-muted-foreground mt-1">
                {isActive ? "Added on top of the current expiry." : "Access expires this many days after renewal."}
              </div>
            </div>

            {form.data_type !== "mca" && (
              <div className="rounded-md border border-border p-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">GST coverage</div>
                <RadioGroup value={form.gst_coverage.type} onValueChange={(v) => setForm({ ...form, gst_coverage: { ...form.gst_coverage, type: v } })} className="grid grid-cols-3 gap-2 mb-3">
                  {[["pan_india", "Pan India"], ["state", "State"], ["city", "City"]].map(([v, l]) => (
                    <label key={v} className={`cursor-pointer rounded-md border p-2 text-center text-sm ${form.gst_coverage.type === v ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`extend-gst-type-${v}`}>
                      <RadioGroupItem value={v} className="hidden" /> {l}
                    </label>
                  ))}
                </RadioGroup>
                {form.gst_coverage.type !== "pan_india" && (
                  <>
                    <Label className="text-xs">States</Label>
                    <MultiSelect
                      options={options.gst_states}
                      values={form.gst_coverage.states}
                      onChange={(v) => setForm({ ...form, gst_coverage: { ...form.gst_coverage, states: v, cities: form.gst_coverage.cities.filter((c) => gstCityOpts.includes(c)) } })}
                      testid="extend-gst-states"
                    />
                    {form.gst_coverage.type === "city" && (
                      <div className="mt-2">
                        <Label className="text-xs">Cities</Label>
                        <MultiSelect
                          options={gstCityOpts}
                          values={form.gst_coverage.cities}
                          onChange={(v) => setForm({ ...form, gst_coverage: { ...form.gst_coverage, cities: v } })}
                          disabled={form.gst_coverage.states.length === 0}
                          testid="extend-gst-cities"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {form.data_type !== "gst" && (
              <div className="rounded-md border border-border p-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">MCA coverage</div>
                <RadioGroup value={form.mca_coverage.type} onValueChange={(v) => setForm({ ...form, mca_coverage: { ...form.mca_coverage, type: v } })} className="grid grid-cols-4 gap-2 mb-3">
                  {[["pan_india", "Pan India"], ["state", "State"], ["district", "District"], ["roc", "ROC"]].map(([v, l]) => (
                    <label key={v} className={`cursor-pointer rounded-md border p-2 text-center text-sm ${form.mca_coverage.type === v ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`extend-mca-type-${v}`}>
                      <RadioGroupItem value={v} className="hidden" /> {l}
                    </label>
                  ))}
                </RadioGroup>
                {(form.mca_coverage.type === "state" || form.mca_coverage.type === "district") && (
                  <>
                    <Label className="text-xs">States</Label>
                    <MultiSelect
                      options={options.mca_states}
                      values={form.mca_coverage.states}
                      onChange={(v) => setForm({ ...form, mca_coverage: { ...form.mca_coverage, states: v, districts: form.mca_coverage.districts.filter((d) => mcaDistrictOpts.includes(d)) } })}
                      testid="extend-mca-states"
                    />
                    {form.mca_coverage.type === "district" && (
                      <div className="mt-2">
                        <Label className="text-xs">Districts</Label>
                        <MultiSelect
                          options={mcaDistrictOpts}
                          values={form.mca_coverage.districts}
                          onChange={(v) => setForm({ ...form, mca_coverage: { ...form.mca_coverage, districts: v } })}
                          disabled={form.mca_coverage.states.length === 0}
                          testid="extend-mca-districts"
                        />
                      </div>
                    )}
                  </>
                )}
                {form.mca_coverage.type === "roc" && (
                  <>
                    <Label className="text-xs">ROCs</Label>
                    <MultiSelect
                      options={options.rocs}
                      values={form.mca_coverage.rocs}
                      onChange={(v) => setForm({ ...form, mca_coverage: { ...form.mca_coverage, rocs: v } })}
                      testid="extend-mca-rocs"
                    />
                  </>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="extend-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="extend-submit">
            {busy ? "Saving..." : isActive ? "Extend" : "Renew"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [credUser, setCredUser] = useState(null);
  const [histUser, setHistUser] = useState(null);
  const [extendUser, setExtendUser] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/customers");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (uid, status) => {
    try { await api.post(`/admin/customers/${uid}/status`, { status }); toast.success(`Customer ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const del = async (uid, hard) => {
    try {
      await api.delete(`/admin/customers/${uid}`, { params: hard ? { hard: true } : {} });
      toast.success(hard ? "Customer permanently deleted (email/phone freed)" : "Customer deleted");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const filtered = useMemo(() => {
    if (!q) return items;
    const t = q.toLowerCase();
    return items.filter((u) => (u.name + u.email + u.company).toLowerCase().includes(t));
  }, [items, q]);

  return (
    <div className="animate-fade-up">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl">Customers</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Manage subscriber accounts. Suspended, deleted, or expired customers cannot access data.</p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company..." className="pl-8 w-72" data-testid="customers-search" />
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Subscription</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const sub = u.subscription;
              const effStatus = u.effective_status || "inactive";
              const isExpired = effStatus === "expired";
              return (
                <tr key={u.id} className="border-t border-border hover:bg-accent/40" data-testid={`customer-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs num text-muted-foreground">{u.email}</div>
                    <div className="text-xs num text-muted-foreground">{u.phone}</div>
                  </td>
                  <td className="px-4 py-3">{u.company}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border capitalize ${statusStyles[effStatus] || ""}`} data-testid={`status-${u.id}`}>
                      {effStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {sub ? (
                      <div>
                        <div className="capitalize">{sub.status} · {sub.data_type?.toUpperCase()} · {sub.duration_days || 30}d</div>
                        {sub.expires_at && (
                          <div className={`num ${isExpired ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                            Expires {new Date(sub.expires_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ) : <span className="text-muted-foreground">None</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end flex-wrap gap-1.5">
                      <Button size="sm" variant="ghost" onClick={() => setCredUser(u)} data-testid={`cred-${u.id}`}>
                        <KeyRound className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setHistUser(u)} data-testid={`hist-${u.id}`}>
                        <History className="h-3.5 w-3.5" />
                      </Button>
                      {(sub || u.status === "active") && (
                        <Button size="sm" variant="outline" onClick={() => setExtendUser(u)} data-testid={`extend-${u.id}`}>
                          <Timer className="h-3.5 w-3.5 mr-1" /> Extend Package
                        </Button>
                      )}
                      {u.status !== "active" && u.status !== "deleted" && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(u.id, "active")} data-testid={`activate-${u.id}`}>
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reactivate
                        </Button>
                      )}
                      {u.status === "active" && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(u.id, "suspended")} data-testid={`suspend-${u.id}`}>
                          <Ban className="h-3.5 w-3.5 mr-1" /> Suspend
                        </Button>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="destructive" data-testid={`delete-${u.id}`} disabled={u.status === "deleted"}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-popover">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Permanently delete {u.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              The account will be marked deleted and the customer can no longer log in. The email <span className="num">{u.email}</span> and phone <span className="num">{u.phone}</span> will be freed so they can register a fresh account with the same details.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del(u.id, true)} data-testid={`confirm-delete-${u.id}`}>Delete permanently</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">No customers found</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>

      <CredentialDialog user={credUser} open={!!credUser} onOpenChange={(o) => !o && setCredUser(null)} />
      <HistoryDialog user={histUser} open={!!histUser} onOpenChange={(o) => !o && setHistUser(null)} />
      <ExtendDialog user={extendUser} open={!!extendUser} onOpenChange={(o) => !o && setExtendUser(null)} onDone={load} />
    </div>
  );
}
